import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { Users, ArrowRight, ArrowLeft, Download, Shield, Home, MapPin, Bed, Building, CheckCircle, Heart, X, RefreshCw, FileText, Phone, Mail } from "lucide-react";
import AnonymityBanner from "@/components/AnonymityBanner";
import NextStepsFooter from "@/components/NextStepsFooter";
import "@/styles/govtech.css";

// Simulated exchange listings storage (in production, this would be a backend)
const getStoredListings = () => {
  try {
    return JSON.parse(localStorage.getItem('tenures_exchange_listings') || '[]');
  } catch {
    return [];
  }
};

const storeListings = (listings) => {
  localStorage.setItem('tenures_exchange_listings', JSON.stringify(listings));
};

// Sample seed data for demo purposes
const SEED_LISTINGS = [
  {
    id: "EX001",
    postcode: "E1",
    currentBedrooms: 3,
    wantedBedrooms: 1,
    propertyType: "house",
    floor: null,
    hasGarden: true,
    hasParking: true,
    petFriendly: true,
    wantedAreas: ["E1", "E2", "E3", "Tower Hamlets"],
    reason: "Children moved out, want to downsize",
    createdAt: new Date().toISOString()
  },
  {
    id: "EX002",
    postcode: "SE15",
    currentBedrooms: 1,
    wantedBedrooms: 2,
    propertyType: "flat",
    floor: "ground",
    hasGarden: false,
    hasParking: false,
    petFriendly: false,
    wantedAreas: ["SE15", "SE16", "Southwark", "Lewisham"],
    reason: "Growing family, need more space",
    createdAt: new Date().toISOString()
  },
  {
    id: "EX003",
    postcode: "N1",
    currentBedrooms: 2,
    wantedBedrooms: 1,
    propertyType: "flat",
    floor: "upper",
    hasGarden: false,
    hasParking: true,
    petFriendly: true,
    wantedAreas: ["N1", "N5", "Islington"],
    reason: "Reduce bills after retirement",
    createdAt: new Date().toISOString()
  }
];

// Initialise with seed data if empty
const initializeListings = () => {
  const existing = getStoredListings();
  if (existing.length === 0) {
    storeListings(SEED_LISTINGS);
  }
};

// Matching algorithm
const findMatches = (myListing, allListings) => {
  return allListings
    .filter(listing => listing.id !== myListing.id)
    .map(listing => {
      let score = 0;
      let matchReasons = [];
      
      // They want what I have
      if (listing.wantedBedrooms === myListing.currentBedrooms) {
        score += 30;
        matchReasons.push(`They want ${myListing.currentBedrooms} bed - you have it!`);
      }
      
      // I want what they have
      if (myListing.wantedBedrooms === listing.currentBedrooms) {
        score += 30;
        matchReasons.push(`You want ${listing.currentBedrooms} bed - they have it!`);
      }
      
      // Area match - they want my area
      const myArea = myListing.postcode.split(/\d/)[0].toUpperCase();
      if (listing.wantedAreas.some(area => area.toUpperCase().includes(myArea) || myArea.includes(area.toUpperCase()))) {
        score += 20;
        matchReasons.push(`They're looking in your area (${myListing.postcode})`);
      }
      
      // Area match - I want their area
      const theirArea = listing.postcode.split(/\d/)[0].toUpperCase();
      if (myListing.wantedAreas.some(area => area.toUpperCase().includes(theirArea) || theirArea.includes(area.toUpperCase()))) {
        score += 20;
        matchReasons.push(`You're looking in their area (${listing.postcode})`);
      }
      
      return {
        ...listing,
        matchScore: score,
        matchReasons,
        isPerfectMatch: score >= 80
      };
    })
    .filter(listing => listing.matchScore > 0)
    .sort((a, b) => b.matchScore - a.matchScore);
};

// Generate Match PDF
const generateMatchPDF = (myListing, matchedListing, contactDetails) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = 20;
  
  // Header
  doc.setFillColor(0, 112, 60);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("TenURes.co.uk", margin, 25);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("MUTUAL EXCHANGE MATCH REPORT", margin, 35);
  
  yPos = 55;
  
  // Authority Stamp
  doc.setFillColor(244, 244, 244);
  doc.rect(margin, yPos, contentWidth, 20, 'F');
  doc.setDrawColor(0, 112, 60);
  doc.setLineWidth(1);
  doc.line(margin, yPos, margin, yPos + 20);
  
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("OFFICER-READY EXCHANGE DOCUMENTATION", margin + 5, yPos + 8);
  doc.setFont("helvetica", "normal");
  doc.text("Generated by TenURes Mutual Exchange Matcher", margin + 5, yPos + 15);
  
  yPos += 30;
  
  // Match Status
  doc.setFillColor(204, 226, 216);
  doc.rect(margin, yPos, contentWidth, 15, 'F');
  doc.setTextColor(0, 112, 60);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("COMPATIBLE MATCH IDENTIFIED", margin + 5, yPos + 10);
  
  yPos += 25;
  
  // Two column layout for properties
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Your Property", margin, yPos);
  doc.text("Their Property", margin + 90, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  // Your property details
  doc.text(`Area: ${myListing.postcode}`, margin, yPos);
  doc.text(`Area: ${matchedListing.postcode}`, margin + 90, yPos);
  yPos += 7;
  
  doc.text(`Bedrooms: ${myListing.currentBedrooms}`, margin, yPos);
  doc.text(`Bedrooms: ${matchedListing.currentBedrooms}`, margin + 90, yPos);
  yPos += 7;
  
  doc.text(`Type: ${myListing.propertyType}`, margin, yPos);
  doc.text(`Type: ${matchedListing.propertyType}`, margin + 90, yPos);
  yPos += 7;
  
  doc.text(`Garden: ${myListing.hasGarden ? 'Yes' : 'No'}`, margin, yPos);
  doc.text(`Garden: ${matchedListing.hasGarden ? 'Yes' : 'No'}`, margin + 90, yPos);
  yPos += 15;
  
  // Contact Details Section
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Exchange Partner Contact Details", margin, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Name: ${contactDetails.partnerName || 'To be shared upon mutual agreement'}`, margin, yPos);
  yPos += 7;
  doc.text(`Contact: ${contactDetails.partnerContact || 'To be shared upon mutual agreement'}`, margin, yPos);
  yPos += 15;
  
  // Next Steps
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Next Steps for Both Parties", margin, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const steps = [
    "1. Both parties contact their Housing Officer to express interest in exchange",
    "2. Request Mutual Exchange application forms from your landlords",
    "3. Arrange property viewings with each other",
    "4. Both landlords must approve the exchange (within 42 days)",
    "5. If approved, agree on a completion date",
    "6. Complete the exchange simultaneously"
  ];
  
  steps.forEach(step => {
    doc.text(step, margin, yPos);
    yPos += 7;
  });
  
  yPos += 10;
  
  // Important Notice
  doc.setFillColor(255, 247, 230);
  doc.rect(margin, yPos, contentWidth, 25, 'F');
  doc.setDrawColor(244, 119, 56);
  doc.setLineWidth(1);
  doc.line(margin, yPos, margin, yPos + 25);
  
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(244, 119, 56);
  doc.text("IMPORTANT:", margin + 5, yPos + 8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(51, 51, 51);
  doc.text("Both landlords must approve. Exchanges can be refused if either party has", margin + 5, yPos + 15);
  doc.text("rent arrears, ASB issues, or if the property would be overcrowded/under-occupied.", margin + 5, yPos + 21);
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(
    `Generated by TenURes.co.uk | ${new Date().toLocaleDateString('en-GB')} | Match Ref: ${myListing.id}-${matchedListing.id}`,
    pageWidth / 2,
    doc.internal.pageSize.getHeight() - 10,
    { align: 'center' }
  );
  
  doc.save("TenURes_Exchange_Match.pdf");
};

// Components
const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

const PropertyCard = ({ listing, isMatch, matchScore, matchReasons, onViewMatch }) => (
  <div className={`govtech-card ${isMatch ? 'border-[#00703C] border-2' : ''}`}>
    {isMatch && (
      <div className="flex items-center gap-2 mb-4">
        <Heart className="text-[#00703C]" size={20} fill="#00703C" />
        <span className="text-sm font-bold text-[#00703C]">{matchScore}% Match</span>
      </div>
    )}
    
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-[#00703C] bg-opacity-10 flex items-center justify-center">
          <Home className="text-[#00703C]" size={24} />
        </div>
        <div>
          <p className="font-bold text-[#333333]">{listing.currentBedrooms} Bed {listing.propertyType}</p>
          <p className="text-sm text-gray-500 flex items-center gap-1">
            <MapPin size={14} /> {listing.postcode} area
          </p>
        </div>
      </div>
      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
        ID: {listing.id}
      </span>
    </div>
    
    <div className="grid grid-cols-2 gap-2 text-sm mb-4">
      <div className="flex items-center gap-2 text-gray-600">
        <Bed size={14} />
        <span>Wants: {listing.wantedBedrooms} bed</span>
      </div>
      <div className="flex items-center gap-2 text-gray-600">
        <Building size={14} />
        <span>{listing.propertyType}</span>
      </div>
      {listing.hasGarden && (
        <div className="flex items-center gap-2 text-gray-600">
          <CheckCircle size={14} className="text-[#00703C]" />
          <span>Garden</span>
        </div>
      )}
      {listing.petFriendly && (
        <div className="flex items-center gap-2 text-gray-600">
          <CheckCircle size={14} className="text-[#00703C]" />
          <span>Pet Friendly</span>
        </div>
      )}
    </div>
    
    {matchReasons && matchReasons.length > 0 && (
      <div className="bg-[#00703C] bg-opacity-5 p-3 mb-4">
        <p className="text-xs font-semibold text-[#00703C] mb-1">Why you match:</p>
        <ul className="text-xs text-gray-700 space-y-1">
          {matchReasons.map((reason, i) => (
            <li key={i}>✓ {reason}</li>
          ))}
        </ul>
      </div>
    )}
    
    {isMatch && (
      <button
        onClick={onViewMatch}
        className="govtech-btn govtech-btn-primary w-full text-sm"
      >
        Express Interest <ArrowRight size={16} className="ml-2" />
      </button>
    )}
  </div>
);

export default function ExchangeMatcher() {
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro, form, matches, contact
  const [myListing, setMyListing] = useState({
    postcode: "",
    currentBedrooms: "",
    wantedBedrooms: "",
    propertyType: "",
    floor: "",
    hasGarden: false,
    hasParking: false,
    petFriendly: false,
    wantedAreas: "",
    reason: ""
  });
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: "",
    phone: "",
    email: ""
  });
  const [submitted, setSubmitted] = useState(false);
  
  useEffect(() => {
    initializeListings();
  }, []);
  
  const handleSubmitListing = () => {
    const newListing = {
      ...myListing,
      id: `EX${Date.now().toString().slice(-6)}`,
      wantedAreas: myListing.wantedAreas.split(',').map(a => a.trim()),
      currentBedrooms: parseInt(myListing.currentBedrooms),
      wantedBedrooms: parseInt(myListing.wantedBedrooms),
      createdAt: new Date().toISOString()
    };
    
    // Store listing
    const listings = getStoredListings();
    listings.push(newListing);
    storeListings(listings);
    
    // Find matches
    const foundMatches = findMatches(newListing, listings);
    setMatches(foundMatches);
    setMyListing(newListing);
    setStep('matches');
  };
  
  const handleExpressInterest = (match) => {
    setSelectedMatch(match);
    setStep('contact');
  };
  
  const handleSubmitContact = () => {
    // Log the match interest (in production, this would notify both parties)
    console.log("[Exchange Interest]", {
      myListing,
      selectedMatch,
      contactDetails: contactForm
    });
    setSubmitted(true);
  };
  
  const handleDownloadPDF = () => {
    generateMatchPDF(myListing, selectedMatch, {
      partnerName: "Pending mutual agreement",
      partnerContact: "Pending mutual agreement"
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/resident')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Back to Portal</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>
      
      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        {/* Authority Stamp */}
        <div className="authority-stamp mb-8">
          <div className="authority-stamp-icon">
            <Shield size={20} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">TenURes Exchange Matcher</p>
            <p className="text-sm text-gray-600">Anonymous, secure matching for social housing tenants</p>
          </div>
        </div>
        
        {/* Intro Step */}
        {step === 'intro' && (
          <div className="govtech-card">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
                <Users className="text-[#00703C]" size={40} />
              </div>
              <h1 className="govtech-heading-l mb-4">Find Your Perfect Exchange Match</h1>
              <p className="text-gray-600 max-w-lg mx-auto">
                Tell us about your property and what you're looking for. We'll match you with other residents 
                who may be interested in swapping.
              </p>
            </div>

            <AnonymityBanner />
            
            {/* Important Disclaimer */}
            <div className="bg-[#F47738] bg-opacity-10 border-2 border-[#F47738] p-5 mb-8">
              <h3 className="font-bold text-[#333333] mb-2 flex items-center gap-2">
                <Shield className="text-[#F47738]" size={20} />
                Important: What We Do (and Don't Do)
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#00703C] flex-shrink-0 mt-0.5" size={16} />
                  <span><strong>We DO:</strong> Match you with residents who are looking for what you have (and vice versa)</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="text-[#D4351C] flex-shrink-0 mt-0.5" size={16} />
                  <span><strong>We DON'T:</strong> Facilitate, arrange, or influence any exchange decisions</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="text-[#D4351C] flex-shrink-0 mt-0.5" size={16} />
                  <span><strong>We DON'T:</strong> Contact your landlord or Housing Officer on your behalf</span>
                </li>
              </ul>
              <p className="text-sm text-[#F47738] font-medium mt-3">
                All communications and the exchange process must go through your Housing Officer. Both landlords must approve any exchange.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 mb-8">
              <h3 className="font-bold text-[#333333] mb-4">How It Works</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00703C] text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <div>
                    <p className="font-semibold text-[#333333]">List Your Property</p>
                    <p className="text-sm text-gray-600">Tell us about your current home and what you're looking for</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00703C] text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <div>
                    <p className="font-semibold text-[#333333]">Get Matched</p>
                    <p className="text-sm text-gray-600">Our algorithm finds residents whose needs match yours</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00703C] text-white flex items-center justify-center font-bold flex-shrink-0">3</div>
                  <div>
                    <p className="font-semibold text-[#333333]">Connect & Exchange</p>
                    <p className="text-sm text-gray-600">Download your Officer-Ready PDF and contact your Housing Officers</p>
                  </div>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setStep('form')}
              className="govtech-btn govtech-btn-primary w-full"
            >
              Start Matching <ArrowRight size={18} className="ml-2" />
            </button>
          </div>
        )}
        
        {/* Form Step */}
        {step === 'form' && (
          <div className="govtech-card">
            <h1 className="govtech-heading-m mb-6">Your Property Details</h1>
            
            <div className="space-y-6">
              {/* Current Property */}
              <div className="bg-gray-50 p-4">
                <h3 className="font-bold text-[#333333] mb-4">Your Current Property</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-1">Postcode Area *</label>
                    <input
                      type="text"
                      className="govtech-input"
                      placeholder="e.g., E1, SE15, N1"
                      value={myListing.postcode}
                      onChange={(e) => setMyListing({...myListing, postcode: e.target.value.toUpperCase()})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-1">Bedrooms *</label>
                    <select
                      className="govtech-input"
                      value={myListing.currentBedrooms}
                      onChange={(e) => setMyListing({...myListing, currentBedrooms: e.target.value})}
                    >
                      <option value="">Select...</option>
                      <option value="1">1 Bedroom</option>
                      <option value="2">2 Bedrooms</option>
                      <option value="3">3 Bedrooms</option>
                      <option value="4">4+ Bedrooms</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-1">Property Type *</label>
                    <select
                      className="govtech-input"
                      value={myListing.propertyType}
                      onChange={(e) => setMyListing({...myListing, propertyType: e.target.value})}
                    >
                      <option value="">Select...</option>
                      <option value="house">House</option>
                      <option value="flat">Flat</option>
                      <option value="maisonette">Maisonette</option>
                      <option value="bungalow">Bungalow</option>
                    </select>
                  </div>
                  {myListing.propertyType === 'flat' && (
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-1">Floor</label>
                      <select
                        className="govtech-input"
                        value={myListing.floor}
                        onChange={(e) => setMyListing({...myListing, floor: e.target.value})}
                      >
                        <option value="">Select...</option>
                        <option value="ground">Ground Floor</option>
                        <option value="upper">Upper Floor</option>
                      </select>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-4 mt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={myListing.hasGarden}
                      onChange={(e) => setMyListing({...myListing, hasGarden: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="text-sm">Has Garden</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={myListing.hasParking}
                      onChange={(e) => setMyListing({...myListing, hasParking: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="text-sm">Has Parking</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={myListing.petFriendly}
                      onChange={(e) => setMyListing({...myListing, petFriendly: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="text-sm">Pet Friendly</span>
                  </label>
                </div>
              </div>
              
              {/* What You're Looking For */}
              <div className="bg-gray-50 p-4">
                <h3 className="font-bold text-[#333333] mb-4">What You're Looking For</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-1">Bedrooms Needed *</label>
                    <select
                      className="govtech-input"
                      value={myListing.wantedBedrooms}
                      onChange={(e) => setMyListing({...myListing, wantedBedrooms: e.target.value})}
                    >
                      <option value="">Select...</option>
                      <option value="1">1 Bedroom</option>
                      <option value="2">2 Bedrooms</option>
                      <option value="3">3 Bedrooms</option>
                      <option value="4">4+ Bedrooms</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-1">Preferred Areas *</label>
                    <input
                      type="text"
                      className="govtech-input"
                      placeholder="e.g., E1, E2, Tower Hamlets"
                      value={myListing.wantedAreas}
                      onChange={(e) => setMyListing({...myListing, wantedAreas: e.target.value})}
                    />
                    <p className="text-xs text-gray-500 mt-1">Separate multiple areas with commas</p>
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-[#333333] mb-1">Reason for Exchange</label>
                  <textarea
                    className="govtech-input min-h-[80px]"
                    placeholder="e.g., Downsizing after children moved out..."
                    value={myListing.reason}
                    onChange={(e) => setMyListing({...myListing, reason: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={() => setStep('intro')}
                  className="govtech-btn govtech-btn-secondary flex-1"
                >
                  <ArrowLeft size={18} className="mr-2" /> Back
                </button>
                <button
                  onClick={handleSubmitListing}
                  disabled={!myListing.postcode || !myListing.currentBedrooms || !myListing.wantedBedrooms || !myListing.propertyType || !myListing.wantedAreas}
                  className={`govtech-btn govtech-btn-primary flex-1 ${(!myListing.postcode || !myListing.currentBedrooms || !myListing.wantedBedrooms || !myListing.propertyType || !myListing.wantedAreas) ? 'opacity-50' : ''}`}
                >
                  Find Matches <ArrowRight size={18} className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Matches Step */}
        {step === 'matches' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="govtech-heading-m">Your Matches</h1>
              <button
                onClick={() => {
                  const listings = getStoredListings();
                  const foundMatches = findMatches(myListing, listings);
                  setMatches(foundMatches);
                }}
                className="text-[#00703C] text-sm font-medium flex items-center gap-1"
              >
                <RefreshCw size={16} /> Refresh
              </button>
            </div>
            
            {/* Reminder Banner */}
            <div className="bg-[#333333] text-white p-4 mb-6">
              <p className="text-sm">
                <strong>Remember:</strong> These are potential matches only. To proceed, contact your Housing Officer 
                and follow your council's official Mutual Exchange process.
              </p>
            </div>
            
            {/* My Listing Summary */}
            <div className="bg-[#00703C] bg-opacity-5 border border-[#00703C] border-opacity-30 p-4 mb-6">
              <p className="text-sm font-semibold text-[#00703C] mb-2">Your Listing</p>
              <p className="text-[#333333]">
                <strong>{myListing.currentBedrooms} bed {myListing.propertyType}</strong> in {myListing.postcode} 
                → Looking for <strong>{myListing.wantedBedrooms} bed</strong> in {Array.isArray(myListing.wantedAreas) ? myListing.wantedAreas.join(', ') : myListing.wantedAreas}
              </p>
            </div>
            
            {matches.length > 0 ? (
              <div className="space-y-4">
                {matches.map((match) => (
                  <PropertyCard
                    key={match.id}
                    listing={match}
                    isMatch={true}
                    matchScore={match.matchScore}
                    matchReasons={match.matchReasons}
                    onViewMatch={() => handleExpressInterest(match)}
                  />
                ))}
              </div>
            ) : (
              <div className="govtech-card text-center py-12">
                <Users className="text-gray-300 mx-auto mb-4" size={48} />
                <h3 className="text-lg font-bold text-[#333333] mb-2">No Matches Yet</h3>
                <p className="text-gray-600 mb-4">
                  We haven't found a compatible match yet, but your listing is now active. 
                  Check back regularly as new residents register.
                </p>
                <p className="text-sm text-gray-500">
                  Your listing ID: <strong>{myListing.id}</strong>
                </p>
              </div>
            )}
            
            <button
              onClick={() => setStep('form')}
              className="govtech-btn govtech-btn-secondary w-full mt-6"
            >
              <ArrowLeft size={18} className="mr-2" /> Edit My Listing
            </button>
          </div>
        )}
        
        {/* Contact Step */}
        {step === 'contact' && selectedMatch && (
          <div className="govtech-card">
            {!submitted ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
                    <Heart className="text-[#00703C]" size={32} fill="#00703C" />
                  </div>
                  <h1 className="govtech-heading-m mb-2">Potential Match Found!</h1>
                  <p className="text-gray-600">
                    Save your details and download the match information to share with your Housing Officer.
                  </p>
                </div>
                
                {/* Important Notice */}
                <div className="bg-[#F47738] bg-opacity-10 border border-[#F47738] p-4 mb-6">
                  <p className="text-sm text-[#333333]">
                    <strong>Important:</strong> TenURes does not facilitate exchanges. Contact your Housing Officer 
                    to start the official Mutual Exchange process. Both landlords must approve any exchange.
                  </p>
                </div>
                
                {/* Match Summary */}
                <div className="bg-gray-50 p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-semibold text-[#333333]">Your Property</p>
                      <p className="text-gray-600">{myListing.currentBedrooms} bed in {myListing.postcode}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-[#333333]">Their Property</p>
                      <p className="text-gray-600">{selectedMatch.currentBedrooms} bed in {selectedMatch.postcode}</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-1">Your Name *</label>
                    <input
                      type="text"
                      className="govtech-input"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      className="govtech-input"
                      placeholder="07xxx xxxxxx"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-1">Email</label>
                    <input
                      type="email"
                      className="govtech-input"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <button
                    onClick={() => setStep('matches')}
                    className="govtech-btn govtech-btn-secondary flex-1"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmitContact}
                    disabled={!contactForm.name || !contactForm.phone}
                    className={`govtech-btn govtech-btn-primary flex-1 ${(!contactForm.name || !contactForm.phone) ? 'opacity-50' : ''}`}
                  >
                    Submit Interest
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-[#00703C] flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-white" size={40} />
                </div>
                <h2 className="govtech-heading-m mb-2">Details Saved!</h2>
                <p className="text-gray-600 mb-6">
                  Download the PDF below and take it to your Housing Officer to start the official exchange process.
                </p>
                
                {/* Reminder */}
                <div className="bg-[#333333] text-white p-4 mb-6 text-left">
                  <h3 className="font-bold mb-2">What to do next:</h3>
                  <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside">
                    <li>Download the Match Information PDF below</li>
                    <li>Contact your Housing Officer to discuss the potential match</li>
                    <li>Request a Mutual Exchange application form from your council</li>
                    <li>Your Housing Officer will guide you through the official process</li>
                  </ol>
                </div>
                
                <div className="space-y-3">
                  <button
                    onClick={handleDownloadPDF}
                    className="govtech-btn govtech-btn-primary w-full"
                  >
                    <Download size={18} className="mr-2" /> Generate Exchange Evidence Report
                  </button>
                  <p className="text-xs text-gray-500 text-center">Your Clearly-Documented Match Summary</p>

                  <NextStepsFooter reviewDays={28} teamName="housing team" />

                  <button
                    onClick={() => navigate('/resident')}
                    className="govtech-btn govtech-btn-secondary w-full"
                  >
                    Return to Portal
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            Exchange matches are suggestions only. Both landlords must approve any exchange.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            © {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk
          </p>
        </div>
      </footer>
    </div>
  );
}
